/**
 * base.ts — shared zod building blocks for the response-shape validation boundary (fetchEnvelope's
 * optional `schema` gate). These mirror the LOCKED cross-cutting shapes in shared/contracts.ts
 * (Provenance / ProvenancedRow / Section<T> / Meta) so per-endpoint "bucket" schemas can compose them
 * instead of re-declaring the envelope plumbing.
 *
 * WHY A GATE, NOT A PARSER: the goal is to catch STRUCTURAL render-shape DRIFT at the API boundary
 * (array vs non-array, object vs primitive, a missing REQUIRED field, a wrong PRIMITIVE type) BEFORE it
 * throws deep in render. It is NOT a value validator and MUST NOT introduce false positives that would
 * break pages that work today.
 *
 * LENIENCY CONVENTIONS (bucket authors: follow these EXACTLY) —
 *   1. Model each field with the EXACT optionality of the contract:
 *        `| null`        => `.nullable()`
 *        optional `?`    => `.optional()`
 *        both            => `.nullable().optional()`
 *   2. Use ONLY primitives: `z.string()`, `z.number()`, `z.boolean()` (and `z.array(...)` / `z.object(...)`).
 *   3. NEVER value-police: do NOT use `z.enum()` / `z.literal()`. A contract union like
 *        `DataStatus | string | null` collapses to `z.string().nullable()`. The server may legitimately
 *        emit a new status/feed value; that is not drift.
 *   4. NEVER `.strict()`. Objects allow unknown/extra keys so the server adding a field is forward-compatible
 *        (the gate returns the ORIGINAL payload untouched — extra keys survive).
 *   5. The gate validates SHAPE and returns the original data; it never strips or transforms.
 *   6. PHI-SAFE ISSUE PATHS: telemetry logs each zod issue's PATH (field names) and CODE. A payload value
 *        or key must NEVER be able to reach issue.path or issue.message, so:
 *          - do NOT use `z.record` / `z.map` / `z.tuple` whose KEYS or per-VALUE failures would surface a
 *            PAYLOAD-DERIVED token (a member id / MRN key) as the first element of issue.path — prefer a
 *            `z.object` of known fields, or a `superRefine` that safeParses each value and emits a STATIC
 *            path token (see member.ts `member360Schema`);
 *          - NEVER interpolate a payload value (or key) into a `superRefine` / custom refinement message.
 *        api.ts is the single SANITIZATION chokepoint (it templates the URL and scrubs each issue-path
 *        segment), but schemas must not RELY on it — emit safe paths at the source too (defense in depth).
 *
 * COMPILE-TIME LINK: each building block carries an `extends` assertion proving the CONTRACT type is
 * assignable to the schema's inferred type — i.e. the schema is never STRICTER than the contract (no
 * false-positive rejection of valid contract data). The reverse direction is intentionally NOT asserted
 * because primitives are deliberately wider than the contract's string-literal unions (rule 3).
 */
import { z } from "zod";
import type { Provenance, ProvenancedRow, Section } from "@shared/contracts";

/**
 * Compile-time guard: fails typecheck unless `A` is assignable to `B`. Exported as the single
 * declaration for every bucket schema (member/caregaps/dataHealth/admin import it) so the guard cannot
 * drift between files (F10 — was redeclared verbatim in five files).
 */
export type AssertExtends<A extends B, B> = A;

/**
 * Provenance roll-up (shared/contracts.ts `Provenance`). dataStatus/provenanceFeed are broad unions in
 * the contract (`DataStatus`, `ProvenanceFeed`) → modeled as plain `z.string()` (rule 3), never enums.
 */
export const provenanceSchema = z.object({
  dataStatus: z.string(),
  provenanceFeed: z.string(),
  gapReason: z.string().nullable().optional(),
  unblockingFeed: z.string().nullable().optional(),
  asOf: z.string().nullable().optional(),
  populationCaveat: z.boolean().optional(),
  rowCount: z.number(),
});
type _ProvenanceLink = AssertExtends<Provenance, z.infer<typeof provenanceSchema>>;

/**
 * Object FRAGMENT (a raw zod shape, not a schema) for the synced provenance columns every domain row
 * carries (shared/contracts.ts `ProvenancedRow`). Spread it into a row schema:
 *   `z.object({ ...provenancedRowShape, condition_key: z.string(), ... })`
 * data_status may be null on the op_* column, so `.nullable()` (required-but-nullable); the other two are
 * optional-and-nullable.
 */
export const provenancedRowShape = {
  data_status: z.string().nullable(),
  provenance_feed: z.string().nullable().optional(),
  gap_reason: z.string().nullable().optional(),
};
type _ProvenancedRowLink = AssertExtends<ProvenancedRow, z.infer<z.ZodObject<typeof provenancedRowShape>>>;

/**
 * `Section<T>` (shared/contracts.ts) — a self-provenanced slice of a composite payload. `status` is a
 * broad union (`SectionStatus`) → `z.string()` (rule 3). `error` is present only on failures → optional.
 * Pass the section's inner-data schema:  `sectionSchema(z.object({ count: z.number() }))`.
 */
export function sectionSchema<T>(inner: z.ZodType<T>): z.ZodType<Section<T>> {
  return z.object({
    status: z.string(),
    data: inner,
    provenance: provenanceSchema,
    error: z.string().optional(),
  }) as unknown as z.ZodType<Section<T>>;
}
